import React, { useEffect, useState, useCallback } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';

const functions = getFunctions();
const createEmployeeStripe    = httpsCallable(functions, 'createEmployeeStripe');
const getEmployeeStripeStatus = httpsCallable(functions, 'getEmployeeStripeStatus');
const getEmployeeTipBalanceFn = httpsCallable(functions, 'getEmployeeTipBalance');
const cashOutTipsFn           = httpsCallable(functions, 'cashOutTips');

/**
 * MyPay — employee self-service payroll panel.
 * Shows pay info and lets the employee connect / complete their Stripe account.
 * Status is fetched live from Stripe on load so the badge always reflects reality.
 */
const MyPay = ({ user, restaurantId }) => {
  const [stripeStatus, setStripeStatus] = useState(null); // null = loading, object = loaded
  const [busy, setBusy]   = useState(false);
  const [error, setError] = useState('');

  // Tip balance + cashout
  const [tipBalance, setTipBalance]       = useState(null); // null = loading
  const [cashoutBusy, setCashoutBusy]     = useState(false);
  const [cashoutSuccess, setCashoutSuccess] = useState(null); // { amountPaidCents }
  const [cashoutError, setCashoutError]   = useState('');

  const loadTipBalance = useCallback(() => {
    if (!user.employeeId || !restaurantId) return;
    getEmployeeTipBalanceFn({ restaurantId, employeeId: user.employeeId })
      .then(res => setTipBalance(res.data))
      .catch(() => setTipBalance({ error: true }));
  }, [user.employeeId, restaurantId]);

  useEffect(() => { loadTipBalance(); }, [loadTipBalance]);

  // On mount: if employee already has a Stripe account ID, check its live status
  useEffect(() => {
    if (!user.stripeAccountId) {
      setStripeStatus({ hasAccount: false });
      return;
    }
    if (!user.employeeId) {
      setStripeStatus({ hasAccount: false });
      return;
    }
    getEmployeeStripeStatus({ restaurantId, employeeId: user.employeeId })
      .then(res => setStripeStatus(res.data))
      .catch(() => setStripeStatus({ hasAccount: true, payoutsEnabled: false, detailsSubmitted: false, onboardingUrl: null, fetchError: true }));
  }, [restaurantId, user.employeeId, user.stripeAccountId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleConnect = async () => {
    setBusy(true);
    setError('');
    try {
      const result = await createEmployeeStripe({
        restaurantId,
        employeeId:   user.employeeId,
        employeeName: `${user.firstName} ${user.lastName}`,
        employeeRole: user.role,
      });
      if (result.data?.onboardingUrl) {
        window.open(result.data.onboardingUrl, '_blank');
      }
      // After opening, refresh the status so the UI reflects the new account
      const statusResult = await getEmployeeStripeStatus({ restaurantId, employeeId: user.employeeId });
      setStripeStatus(statusResult.data);
    } catch (err) {
      setError(err.message || 'Failed to connect Stripe. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const handleContinueSetup = () => {
    if (stripeStatus?.onboardingUrl) {
      window.open(stripeStatus.onboardingUrl, '_blank');
    }
  };

  const handleCashOut = async () => {
    if (!user.employeeId) return;
    setCashoutBusy(true);
    setCashoutError('');
    setCashoutSuccess(null);
    try {
      const res = await cashOutTipsFn({ restaurantId, employeeId: user.employeeId });
      setCashoutSuccess(res.data);
      loadTipBalance(); // refresh balance
    } catch (err) {
      setCashoutError(err.message || 'Cashout failed. Try again.');
    } finally {
      setCashoutBusy(false);
    }
  };

  const renderStripeSection = () => {
    if (stripeStatus === null) {
      return <p className="text-sm text-neutral-400">Checking Stripe status…</p>;
    }

    if (!stripeStatus.hasAccount) {
      return (
        <>
          <button
            onClick={handleConnect}
            disabled={busy || !user.employeeId}
            className="px-5 py-2.5 text-sm font-semibold bg-violet-600 text-white rounded-lg hover:bg-violet-500 disabled:opacity-50 transition-colors"
          >
            {busy ? 'Opening Stripe…' : 'Connect Stripe Account'}
          </button>
          {!user.employeeId && (
            <p className="text-sm text-neutral-400 mt-2">Your employee profile isn't linked yet. Contact your manager.</p>
          )}
        </>
      );
    }

    if (stripeStatus.payoutsEnabled) {
      return (
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" />
          <span className="text-sm text-emerald-700 font-medium">Stripe connected — payouts enabled</span>
        </div>
      );
    }

    // Account exists but onboarding not complete
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-amber-400 inline-block" />
          <span className="text-sm text-amber-700 font-medium">
            {stripeStatus.detailsSubmitted ? 'Stripe account under review' : 'Stripe setup incomplete'}
          </span>
        </div>
        <p className="text-sm text-neutral-400">
          {stripeStatus.detailsSubmitted
            ? 'Your details were submitted. Stripe is reviewing your account — payouts will be enabled once approved.'
            : 'You started Stripe setup but did not finish. Open the link below to complete it.'}
        </p>
        {!stripeStatus.detailsSubmitted && stripeStatus.onboardingUrl && (
          <button
            onClick={handleContinueSetup}
            disabled={busy}
            className="px-5 py-2.5 text-sm font-semibold bg-amber-600 text-white rounded-lg hover:bg-amber-500 disabled:opacity-50 transition-colors"
          >
            Continue Stripe Setup
          </button>
        )}
        {stripeStatus.fetchError && (
          <p className="text-sm text-neutral-400">Could not reach Stripe — status may be outdated.</p>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-5 max-w-lg">

      {/* Pay summary */}
      <div className="bg-white border border-neutral-200 rounded-xl p-5">
        <h3 className="text-sm font-semibold text-neutral-700 mb-3">Pay Information</h3>
        <div className="space-y-2">
          <Row label="Pay Type" value={user.payType === 'salary' ? 'Salary' : 'Hourly'} />
          {user.payType === 'salary' ? (
            <Row label="Annual Salary" value={`$${(user.annualSalary || 0).toLocaleString()}`} />
          ) : (
            <Row label="Hourly Rate" value={`$${(user.hourlyRate || 0).toFixed(2)} / hr`} />
          )}
          {user.position   && <Row label="Position"   value={user.position}   />}
          {user.department && <Row label="Department" value={user.department} />}
        </div>
      </div>

      {/* Stripe payouts */}
      <div className="bg-white border border-neutral-200 rounded-xl p-5">
        <h3 className="text-sm font-semibold text-neutral-700 mb-1">Direct Deposit (Stripe)</h3>
        <p className="text-sm text-neutral-400 mb-4">
          Connect a Stripe account to receive payouts directly. You control your own banking info — no one else can connect this for you.
        </p>
        {renderStripeSection()}
        {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
      </div>

      {/* Tip balance + cashout */}
      <div className="bg-white border border-neutral-200 rounded-xl p-5">
        <h3 className="text-sm font-semibold text-neutral-700 mb-1">Tip Balance</h3>
        <p className="text-sm text-neutral-400 mb-1">
          Electronic tips credited to you. Cash out anytime directly to your bank account.
        </p>
        <div className="bg-neutral-50 border border-neutral-200 rounded-lg px-3 py-2.5 mb-4 space-y-1">
          <p className="text-xs font-semibold text-neutral-600">How it works</p>
          <p className="text-xs text-neutral-500 leading-snug">
            When you cash out, GoHost transfers your tip balance from the restaurant's account to your connected
            Stripe Express account, which then automatically deposits to your bank — usually within 1–2 business days.
          </p>
          <p className="text-xs text-neutral-500 leading-snug">
            <span className="font-semibold text-neutral-700">$2.00 processing fee</span> is deducted from your tip balance per cashout.
            Your bank account must be connected via Stripe below. Tip balance must exceed $2.00 to cash out.
          </p>
        </div>

        {tipBalance === null && (
          <p className="text-sm text-neutral-400">Loading tip balance…</p>
        )}

        {tipBalance?.error && (
          <p className="text-sm text-red-500">Could not load tip balance.</p>
        )}

        {tipBalance && !tipBalance.error && (
          <div className="space-y-4">
            {/* Distribution mode context */}
            {tipBalance.tipDistributionMode === 'pool' && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2.5">
                <p className="text-xs font-semibold text-blue-800">Tip Pool</p>
                <p className="text-xs text-blue-600 mt-0.5">
                  Tips are pooled across your team and distributed by hours worked each period.
                  {tipBalance.minHoursForPool > 0 && (
                    <span className="block mt-0.5 font-medium">
                      You must work {tipBalance.minHoursForPool}+ hours in a period to receive a pool share.
                    </span>
                  )}
                  {!tipBalance.inPool && (
                    <span className="block mt-0.5 text-amber-700 font-medium">
                      You are not currently in the tip pool — contact your manager.
                    </span>
                  )}
                </p>
              </div>
            )}
            {tipBalance.tipDistributionMode === 'share' && (
              <div className="bg-neutral-50 border border-neutral-200 rounded-lg px-3 py-2.5">
                <p className="text-xs text-neutral-500">
                  Tips are distributed by your manager each period based on your role.
                </p>
              </div>
            )}

            {/* Balance display */}
            <div className="flex items-end gap-2">
              <span className="text-3xl font-bold text-neutral-900">
                ${tipBalance.totalDollars.toFixed(2)}
              </span>
              <span className="text-sm text-neutral-400 mb-1">available</span>
            </div>

            {tipBalance.count === 0 ? (
              <p className="text-sm text-neutral-400">
                {tipBalance.pendingCount > 0
                  ? `${tipBalance.pendingCount} tip${tipBalance.pendingCount !== 1 ? 's' : ''} pending — available once your shift is closed.`
                  : 'No electronic tips available to cash out yet.'}
              </p>
            ) : (
              <p className="text-sm text-neutral-500">
                {tipBalance.count} distribution{tipBalance.count !== 1 ? 's' : ''} from your manager
              </p>
            )}

            {/* Success banner */}
            {cashoutSuccess && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3">
                <p className="text-sm font-semibold text-emerald-800">
                  Cashout sent — ${(cashoutSuccess.amountPaidCents / 100).toFixed(2)} on its way to your bank.
                </p>
                <p className="text-xs text-emerald-600 mt-0.5">Usually arrives within 1–2 business days.</p>
              </div>
            )}

            {/* Error */}
            {cashoutError && (
              <p className="text-sm text-red-600">{cashoutError}</p>
            )}

            {/* Cashout button */}
            {tipBalance.canCashOut && !cashoutSuccess && (
              <div className="space-y-1">
                <button
                  onClick={handleCashOut}
                  disabled={cashoutBusy || !stripeStatus?.payoutsEnabled}
                  className="w-full py-2.5 text-sm font-semibold bg-emerald-600 text-white rounded-lg hover:bg-emerald-500 disabled:opacity-50 transition-colors"
                >
                  {cashoutBusy
                    ? 'Processing…'
                    : `Cash Out $${((tipBalance.totalCents - tipBalance.cashoutFeeCents) / 100).toFixed(2)} (after $${(tipBalance.cashoutFeeCents / 100).toFixed(2)} fee)`}
                </button>
                {!stripeStatus?.payoutsEnabled && (
                  <p className="text-xs text-amber-600 text-center">Complete Stripe setup above to enable cashouts.</p>
                )}
              </div>
            )}

            {!tipBalance.canCashOut && tipBalance.totalCents > 0 && (
              <p className="text-xs text-neutral-400">
                Balance too low to cover the ${(tipBalance.cashoutFeeCents / 100).toFixed(2)} cashout fee.
              </p>
            )}
          </div>
        )}
      </div>

      {/* Pay history placeholder */}
      <div className="flex flex-col items-center justify-center h-40 border-2 border-dashed border-neutral-200 rounded-xl">
        <p className="text-sm font-medium text-neutral-400">Pay History</p>
        <p className="text-sm text-neutral-300 mt-1">Pay period summaries coming soon.</p>
      </div>
    </div>
  );
};

const Row = ({ label, value }) => (
  <div className="flex justify-between text-sm">
    <span className="text-neutral-500">{label}</span>
    <span className="font-medium text-neutral-800">{value}</span>
  </div>
);

export default MyPay;
